/**
 * ApplicationView — ADDITIVE applications & diagrams view (task 15.5; Req 16.2,
 * 16.7, 18.1).
 *
 * Renders, for one selected Scan_Run, the set of applications the Repo
 * Knowledge Library classified plus the Mermaid diagram artifacts the Library
 * shell wrote alongside the per-run `dashboard-index.json`:
 *
 *  - The application list is derived purely from the already-loaded
 *    {@link DashboardLibraryIndex} (`repos[].application`): distinct, sorted,
 *    real application names with their member repositories. The literal
 *    `"unknown"` is the explicit "no application" fallback (Req 18.6), so it is
 *    excluded from the real-application list and instead surfaced as a single
 *    muted "Unassigned" count.
 *  - The org-wide diagram (`diagrams/org-wide.mmd`) and each per-application
 *    diagram (`diagrams/application/<safeObjectKey(app)>.mmd`) are fetched as
 *    `text/plain` Mermaid source from the `library/` prefix and rendered.
 *
 * Mermaid rendering: `mermaid` is intentionally NOT a dependency of this SPA,
 * so rather than pull in a heavy new dependency this view renders each
 * diagram's raw `.mmd` source in a clearly-labeled `<pre>` code block (the
 * "Mermaid source"). The view is fully functional this way; if `mermaid` is
 * adopted later, {@link DiagramPanel} is the single place to swap in a rendered
 * `<svg>`.
 *
 * Graceful absence (Req 18.6): when there is no Library document for the run
 * (`libraryIndex` is `null`) the view shows a friendly "no applications /
 * diagrams" message and never attempts a fetch. Each diagram fetches
 * independently and a missing artifact (404 / network / parse) degrades to a
 * per-diagram "unavailable" notice — the view never crashes.
 *
 * This component is strictly additive: it reads the Library document the
 * existing {@link useRunLoader} already loads and the diagram artifacts the
 * Python shell already writes. It does not modify any existing type, route, or
 * the query layer. The fetch implementation and the diagram-URL builder are
 * injectable for testing.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { DashboardLibraryIndex, LibraryRepoEntry } from "../types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** The literal explicit "no application" fallback value (Req 18.6). */
const UNASSIGNED_APPLICATION = "unknown";

/** Shown when the selected run has no Library document at all (graceful absence). */
export const NO_LIBRARY_MESSAGE =
  "No applications or diagrams are available for this run.";

/** Shown while a diagram's Mermaid source is being fetched. */
export const DIAGRAM_LOADING_MESSAGE = "Loading diagram\u2026";

/** Shown when a diagram artifact could not be loaded (missing / failed). */
export const DIAGRAM_UNAVAILABLE_MESSAGE = "This diagram is unavailable.";

// ---------------------------------------------------------------------------
// Key sanitization + URL building
// ---------------------------------------------------------------------------

/**
 * TypeScript mirror of `repo_intelligence.handlers._common.safe_object_key`:
 * replace every character outside `[A-Za-z0-9._-]` with `_`, and map the empty
 * string to the literal `"repo"`. This MUST match the Python sanitizer exactly
 * so the per-application diagram key the SPA requests is byte-identical to the
 * one the Library shell wrote.
 */
export function safeObjectKey(name: string): string {
  let out = "";
  for (const ch of name) {
    out += /[A-Za-z0-9._-]/.test(ch) ? ch : "_";
  }
  return out === "" ? "repo" : out;
}

/**
 * Build the URL for a diagram artifact under a run's `library/` prefix. The
 * `relativePath` is the part after `diagrams/` (e.g. `org-wide.mmd` or
 * `application/<safe>.mmd`). The run-date and run-id path segments are
 * URL-encoded; the path is root-absolute so it resolves against the deployed
 * origin regardless of the current client route and maps to the CloudFront
 * `library/*` cache behavior (mirrors {@link buildLibraryIndexUrl}).
 */
export function buildDiagramUrl(
  runDate: string,
  runId: string,
  relativePath: string,
): string {
  const date = encodeURIComponent(runDate);
  const id = encodeURIComponent(runId);
  return `/library/run-date=${date}/run-id=${id}/diagrams/${relativePath}`;
}

// ---------------------------------------------------------------------------
// Derivation helpers
// ---------------------------------------------------------------------------

/** One real application and the repositories the Library mapped to it. */
export interface ApplicationGroup {
  name: string;
  repos: LibraryRepoEntry[];
}

/**
 * Derive the distinct, sorted real applications (excluding the `"unknown"`
 * fallback) with their member repositories, plus the count of repositories that
 * are unassigned (`application === "unknown"`). Pure and total.
 */
export function deriveApplications(index: DashboardLibraryIndex): {
  applications: ApplicationGroup[];
  unassignedCount: number;
} {
  const byApp = new Map<string, LibraryRepoEntry[]>();
  let unassignedCount = 0;

  for (const entry of index.repos) {
    if (entry.application === UNASSIGNED_APPLICATION) {
      unassignedCount += 1;
      continue;
    }
    const members = byApp.get(entry.application);
    if (members) {
      members.push(entry);
    } else {
      byApp.set(entry.application, [entry]);
    }
  }

  const applications: ApplicationGroup[] = [...byApp.entries()]
    .map(([name, repos]) => ({
      name,
      repos: [...repos].sort((a, b) =>
        a.repoName.toLowerCase().localeCompare(b.repoName.toLowerCase()),
      ),
    }))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  return { applications, unassignedCount };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link ApplicationView}. */
export interface ApplicationViewProps {
  /** Selected run-date (YYYY-MM-DD) — used to build diagram URLs. */
  runDate: string;
  /** Selected run-id — used to build diagram URLs. */
  runId: string;
  /**
   * The per-run Library document (from {@link useRunLoader}), or `null` when
   * the run has no Library artifact. `null` renders the graceful-absence
   * message and skips all diagram fetches.
   */
  libraryIndex: DashboardLibraryIndex | null;
  /** Fetch implementation; defaults to the global `fetch`. Injected in tests. */
  fetchFn?: typeof fetch;
  /** Override the diagram URL builder; defaults to {@link buildDiagramUrl}. */
  buildUrl?: (runDate: string, runId: string, relativePath: string) => string;
  /** Called when the user navigates back to the list. */
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Applications & diagrams view for one Scan_Run. Lists the classified
 * applications and renders the org-wide and per-application Mermaid diagrams as
 * labeled source blocks, degrading gracefully when artifacts are absent.
 */
export default function ApplicationView({
  runDate,
  runId,
  libraryIndex,
  fetchFn,
  buildUrl = buildDiagramUrl,
  onClose,
}: ApplicationViewProps) {
  const derived = useMemo(
    () => (libraryIndex ? deriveApplications(libraryIndex) : null),
    [libraryIndex],
  );

  if (!libraryIndex || !derived) {
    return (
      <section aria-label="Applications and diagrams" className="repo-detail">
        <Header onClose={onClose} />
        <p role="status" className="repo-detail__message">
          {NO_LIBRARY_MESSAGE}
        </p>
      </section>
    );
  }

  const { applications, unassignedCount } = derived;

  return (
    <section aria-label="Applications and diagrams" className="repo-detail">
      <Header onClose={onClose} />

      {/* Org-wide diagram is always written by the Library shell (Req 16.2). */}
      <section className="repo-detail__section" aria-label="Organization-wide diagram">
        <h3 className="repo-detail__section-title">Organization-wide diagram</h3>
        <DiagramPanel
          title="Org-wide"
          url={buildUrl(runDate, runId, "org-wide.mmd")}
          fetchFn={fetchFn}
        />
      </section>

      {/* Applications list + per-application diagram (Req 16.7, 18.1). */}
      <section className="repo-detail__section" aria-label="Applications">
        <h3 className="repo-detail__section-title">
          Applications ({applications.length})
        </h3>

        {applications.length === 0 ? (
          <p role="status" className="repo-detail__message">
            No applications were classified for this run.
          </p>
        ) : (
          <ul className="repo-detail__list">
            {applications.map((app) => (
              <li
                key={app.name}
                className="repo-detail__nested-item"
                aria-label={`Application ${app.name}`}
              >
                <h4 className="repo-detail__label">
                  {app.name}{" "}
                  <span className="repo-detail__muted">
                    ({app.repos.length}{" "}
                    {app.repos.length === 1 ? "repository" : "repositories"})
                  </span>
                </h4>
                <ul className="repo-detail__list">
                  {app.repos.map((repo) => (
                    <li
                      key={`${repo.repoName}\u0000${repo.scanId}`}
                      className="repo-detail__list-item"
                    >
                      {repo.repoName}
                    </li>
                  ))}
                </ul>
                <DiagramPanel
                  title={`${app.name} diagram`}
                  url={buildUrl(
                    runDate,
                    runId,
                    `application/${safeObjectKey(app.name)}.mmd`,
                  )}
                  fetchFn={fetchFn}
                />
              </li>
            ))}
          </ul>
        )}

        {unassignedCount > 0 && (
          <p className="repo-detail__muted">
            {unassignedCount} unassigned{" "}
            {unassignedCount === 1 ? "repository" : "repositories"} (no
            application mapped).
          </p>
        )}
      </section>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

/** Title row with an optional back-to-list control. */
function Header({ onClose }: { onClose?: () => void }) {
  return (
    <header className="repo-detail__header">
      <h2 className="repo-detail__title">Applications &amp; diagrams</h2>
      {onClose && (
        <button type="button" onClick={onClose} className="repo-detail__close">
          Back to list
        </button>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// DiagramPanel — fetch + render a single .mmd artifact
// ---------------------------------------------------------------------------

type DiagramState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; source: string };

/**
 * Fetch a single diagram's `.mmd` Mermaid source and render it. Because
 * `mermaid` is not a dependency, the source is shown verbatim in a labeled
 * `<pre>` block. A missing / failed artifact degrades to an "unavailable"
 * notice; the panel never crashes (graceful absence — Req 18.6).
 */
export function DiagramPanel({
  title,
  url,
  fetchFn,
}: {
  title: string;
  url: string;
  fetchFn?: typeof fetch;
}) {
  const [state, setState] = useState<DiagramState>({ status: "loading" });
  // Keep the latest fetchFn without re-running on every render identity change.
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });

    const doFetch = fetchRef.current ?? fetch;

    void (async () => {
      try {
        const response = await doFetch(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`status ${response.status}`);
        }
        const source = await response.text();
        setState({ status: "loaded", source });
      } catch {
        // Superseded fetch (newer url) aborts us — drop silently.
        if (controller.signal.aborted) {
          return;
        }
        setState({ status: "error" });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [url]);

  if (state.status === "loading") {
    return (
      <p role="status" className="repo-detail__message">
        {DIAGRAM_LOADING_MESSAGE}
      </p>
    );
  }

  if (state.status === "error") {
    return (
      <p role="status" className="repo-detail__placeholder">
        {DIAGRAM_UNAVAILABLE_MESSAGE}
      </p>
    );
  }

  return (
    <figure className="application-view__diagram" aria-label={title}>
      <figcaption className="repo-detail__muted">
        Mermaid source ({title})
      </figcaption>
      <pre className="application-view__diagram-source">
        <code>{state.source}</code>
      </pre>
    </figure>
  );
}
